import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLinkIcon, RefreshCwIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { money } from '@/lib/report-window';
import {
  LEAD_SUBMISSION_STATUS,
  getLeadSubmission,
  getPaymentSubmission,
  listLeadSubmissions,
  listPaymentSubmissions,
  reprocessLeadSubmission,
  reprocessPaymentSubmission,
  salesforceRecordUrl,
  type LeadSubmissionRow,
  type LeadSubmissionStatus,
  type PaymentSubmissionRow,
} from '@/lib/submissions';

/** The two tabs search independently, each under its own URL key. */
const SUBMISSION_SEARCH_KEYS = { leads: 'leadsQ', payments: 'paymentsQ' } as const;

/** Rows per page in either table. */
const PAGE_SIZE = 25;

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** How each lead status reads and colours in the table. */
const LEAD_STATUS_TONE: Readonly<Record<LeadSubmissionStatus, { label: string; tone: Tone }>> = {
  processed: { label: 'Processed', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  quarantined: { label: 'Spam held', tone: 'warning' },
  pending: { label: 'Pending', tone: 'neutral' },
};

/** How each payment outcome status reads and colours. */
function paymentStatusChip(status: string): { label: string; tone: Tone } {
  switch (status) {
    case 'processed':
      return { label: 'Processed', tone: 'success' };
    case 'expense-failed':
    case 'error':
      return { label: status === 'error' ? 'Error' : 'Expense failed', tone: 'danger' };
    case 'opportunity-not-found':
      return { label: 'No opportunity', tone: 'warning' };
    case 'ambiguous':
      return { label: 'Ambiguous contract', tone: 'warning' };
    case 'ignored':
      return { label: 'Ignored', tone: 'neutral' };
    default:
      return { label: 'Received', tone: 'neutral' };
  }
}

/** Short date-time label. */
function when(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** A label/value row in a detail drawer. */
function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex gap-3 border-b border-portal-line py-2 last:border-0">
      <span className="w-40 shrink-0 text-portal-micro text-portal-muted">{label}</span>
      <span className="min-w-0 flex-1 text-portal-small break-words text-portal-ink">{children ?? '—'}</span>
    </div>
  );
}

// ─── Leads tab ────────────────────────────────────────────────────────────────

const LEAD_STATUS_FILTERS: readonly { key: LeadSubmissionStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: LEAD_SUBMISSION_STATUS.processed, label: 'Processed' },
  { key: LEAD_SUBMISSION_STATUS.failed, label: 'Failed' },
  { key: LEAD_SUBMISSION_STATUS.quarantined, label: 'Spam held' },
  { key: LEAD_SUBMISSION_STATUS.pending, label: 'Pending' },
];

function LeadSubmissionsTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LeadSubmissionStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ id: string; release: boolean } | null>(null);
  const { search, setSearch, debounced } = useUrlSearch(SUBMISSION_SEARCH_KEYS.leads);

  const list = useQuery({
    queryKey: ['submission-leads', status, page, debounced],
    queryFn: ({ signal }) =>
      listLeadSubmissions(
        { page, limit: PAGE_SIZE, ...(status !== 'all' ? { status } : {}), ...(debounced ? { search: debounced } : {}) },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const detail = useQuery({
    queryKey: ['submission-lead', openId],
    queryFn: ({ signal }) => getLeadSubmission(openId as string, signal),
    enabled: openId !== null,
  });

  const reprocess = useMutation({
    mutationFn: ({ id, release }: { id: string; release: boolean }) => reprocessLeadSubmission(id, release),
    onSuccess: () => {
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['submission-leads'] });
      if (openId) void queryClient.invalidateQueries({ queryKey: ['submission-lead', openId] });
    },
  });

  const rows = list.data?.items ?? [];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search lead submissions"
          placeholder="Name, email, company or form"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl<LeadSubmissionStatus | 'all'>
          label="Status"
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          options={LEAD_STATUS_FILTERS.map((f) => ({ key: f.key, label: f.label }))}
        />
      </div>

      {list.isError ? (
        <ErrorNotice title="Submissions unavailable" message={errorMessage(list.error)} />
      ) : list.isPending ? (
        <LoadingState label="Loading submissions…" />
      ) : rows.length === 0 && debounced !== '' ? (
        <Panel>
          <EmptyState title="No lead submissions match" message="Try a different search." />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            title="No lead submissions"
            message={status === 'all' ? 'Website form submissions appear here as they arrive.' : 'No submissions with this status.'}
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={['Arrived', 'Lead', 'Company', 'Form', 'Source', 'Status']}
            minWidth={860}
            cards={rows.map((row: LeadSubmissionRow) => ({
              key: row.id,
              title: row.name || '—',
              ...(row.email ? { subtitle: row.email } : {}),
              fields: [
                { label: 'Arrived', value: when(row.createdAt) },
                { label: 'Status', value: <Pill tone={LEAD_STATUS_TONE[row.status].tone}>{LEAD_STATUS_TONE[row.status].label}</Pill> },
                { label: 'Company', value: row.company || '—' },
                { label: 'Form', value: row.formKey || '—' },
                { label: 'Source', value: row.channel || row.leadSource || '—' },
              ],
              onOpen: () => setOpenId(row.id),
            }))}
          >
            {rows.map((row: LeadSubmissionRow) => {
              const chip = LEAD_STATUS_TONE[row.status];
              return (
                <tr
                  key={row.id}
                  className="cursor-pointer border-t border-portal-line align-middle hover:bg-portal-hover"
                  onClick={() => setOpenId(row.id)}
                >
                  <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">{when(row.createdAt)}</td>
                  <td className="p-3 text-portal-small">
                    <span className="block font-bold text-portal-ink">{row.name || '—'}</span>
                    {row.email ? <span className="block text-portal-micro text-portal-muted">{row.email}</span> : null}
                  </td>
                  <td className="p-3 text-portal-small">{row.company || '—'}</td>
                  <td className="p-3 text-portal-small text-portal-muted">{row.formKey || '—'}</td>
                  <td className="p-3 text-portal-small text-portal-muted">{row.channel || row.leadSource || '—'}</td>
                  <td className="p-3 text-portal-small">
                    <Pill tone={chip.tone}>{chip.label}</Pill>
                  </td>
                </tr>
              );
            })}
          </DataTable>
          <Pagination
            page={list.data.pagination.page}
            totalPages={list.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(list.data.pagination.page - 1) * PAGE_SIZE + 1}–${
              (list.data.pagination.page - 1) * PAGE_SIZE + rows.length
            } of ${list.data.pagination.total.toLocaleString()}`}
            className="mt-3"
          />
        </>
      )}

      <Drawer
        open={openId !== null}
        title={detail.data?.name || 'Lead submission'}
        subtitle={detail.data ? `${detail.data.formKey ?? 'form'} · ${when(detail.data.createdAt)}` : undefined}
        onClose={() => setOpenId(null)}
        footer={
          detail.data ? (
            detail.data.status === 'processed' ? (
              <p className="text-portal-micro text-portal-muted">
                Already processed — reprocessing is disabled to avoid a duplicate Salesforce Lead.
              </p>
            ) : (
              <Button
                variant={detail.data.status === 'quarantined' ? 'primary' : 'secondary'}
                onClick={() =>
                  setConfirm({ id: detail.data!.id, release: detail.data!.status === 'quarantined' })
                }
              >
                <RefreshCwIcon className="size-4" aria-hidden="true" />
                {detail.data.status === 'quarantined' ? 'Release & process' : 'Reprocess'}
              </Button>
            )
          ) : null
        }
      >
        {detail.isPending ? (
          <LoadingState label="Loading…" />
        ) : detail.isError ? (
          <ErrorNotice title="Could not load" message={errorMessage(detail.error)} />
        ) : detail.data ? (
          <div>
            <Field label="Status">
              <Pill tone={LEAD_STATUS_TONE[detail.data.status].tone}>
                {LEAD_STATUS_TONE[detail.data.status].label}
              </Pill>
            </Field>
            <Field label="Email">{detail.data.email || '—'}</Field>
            <Field label="Company">{detail.data.company || '—'}</Field>
            <Field label="Lead source">{detail.data.leadSource || '—'}</Field>
            <Field label="Channel">{detail.data.channel || '—'}</Field>
            <Field label="Sales email">{detail.data.salesEmailSent ? 'Sent' : 'Not sent'}</Field>
            <Field label="Pardot handler">
              {detail.data.pardotHandlerStatus != null ? String(detail.data.pardotHandlerStatus) : '—'}
            </Field>
            {detail.data.error ? (
              <Field label="Error">
                <span className="text-rose-700">{detail.data.error}</span>
              </Field>
            ) : null}
            {detail.data.spam ? (
              <Field label="Spam">
                {String((detail.data.spam as { verdict?: unknown }).verdict ?? '—')}
                {Array.isArray((detail.data.spam as { signals?: unknown }).signals)
                  ? ` · ${((detail.data.spam as { signals: unknown[] }).signals).join(', ')}`
                  : ''}
              </Field>
            ) : null}
            {(() => {
              const ctx = (detail.data.context ?? {}) as Record<string, unknown>;
              const val = (v: unknown): string => (v == null || v === '' ? '—' : String(v));
              return (
                <>
                  <h3 className="mt-4 mb-1 text-portal-micro font-bold text-portal-muted uppercase">Source</h3>
                  <Field label="IP address">{val(ctx.ipAddress)}</Field>
                  <Field label="Country">{val(ctx.country)}</Field>
                  <Field label="Referrer">{val(ctx.referrer ?? ctx.landingReferrer)}</Field>
                  <Field label="User agent">{val(ctx.userAgent)}</Field>
                  <Field label="CleanTalk">{val(ctx.cleantalk)}</Field>
                </>
              );
            })()}
            <h3 className="mt-4 mb-1 text-portal-micro font-bold text-portal-muted uppercase">Submitted fields</h3>
            {Object.entries(detail.data.fields).map(([key, value]) => (
              <Field key={key} label={key}>
                {value == null || value === '' ? '—' : String(value)}
              </Field>
            ))}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.release ? 'Release this lead?' : 'Reprocess this lead?'}
        message={
          confirm?.release
            ? 'This bypasses the spam gate and pushes the lead through the full pipeline — it creates the Salesforce Lead and sends the sales email.'
            : 'This re-runs the pipeline for this submission (Salesforce Lead, qualification, sales email).'
        }
        confirmLabel={confirm?.release ? 'Release & process' : 'Reprocess'}
        pending={reprocess.isPending}
        error={reprocess.isError ? errorMessage(reprocess.error) : undefined}
        onConfirm={() => {
          if (confirm) reprocess.mutate(confirm);
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

// ─── Payments tab ─────────────────────────────────────────────────────────────

const PAYMENT_STATUS_FILTERS: readonly { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'processed', label: 'Processed' },
  { key: 'expense-failed', label: 'Expense failed' },
  { key: 'opportunity-not-found', label: 'No opportunity' },
  { key: 'ambiguous', label: 'Ambiguous' },
];

function PaymentSubmissionsTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const { search, setSearch, debounced } = useUrlSearch(SUBMISSION_SEARCH_KEYS.payments);

  const list = useQuery({
    queryKey: ['submission-payments', status, page, debounced],
    queryFn: ({ signal }) =>
      listPaymentSubmissions(
        { page, limit: PAGE_SIZE, ...(status !== 'all' ? { status } : {}), ...(debounced ? { search: debounced } : {}) },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const detail = useQuery({
    queryKey: ['submission-payment', openId],
    queryFn: ({ signal }) => getPaymentSubmission(openId as string, signal),
    enabled: openId !== null,
  });

  const reprocess = useMutation({
    mutationFn: (id: string) => reprocessPaymentSubmission(id),
    onSuccess: () => {
      setConfirmId(null);
      void queryClient.invalidateQueries({ queryKey: ['submission-payments'] });
      if (openId) void queryClient.invalidateQueries({ queryKey: ['submission-payment', openId] });
    },
  });

  const rows = list.data?.items ?? [];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search payment submissions"
          placeholder="Vendor, email, contract or submission ID"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl<string>
          label="Status"
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          options={PAYMENT_STATUS_FILTERS.map((f) => ({ key: f.key, label: f.label }))}
        />
      </div>

      {list.isError ? (
        <ErrorNotice title="Submissions unavailable" message={errorMessage(list.error)} />
      ) : list.isPending ? (
        <LoadingState label="Loading submissions…" />
      ) : rows.length === 0 && debounced !== '' ? (
        <Panel>
          <EmptyState title="No payment submissions match" message="Try a different search." />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            title="No payment submissions"
            message={status === 'all' ? 'Facilitator and assistant payment requests appear here as they arrive.' : 'No submissions with this status.'}
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={['Received', 'Vendor', 'Form', 'Contract', 'Amount', 'Status']}
            rightAlignedHeaders={[4]}
            minWidth={860}
            cards={rows.map((row: PaymentSubmissionRow) => ({
              key: row.id,
              title: row.vendorName || '—',
              ...(row.vendorEmail ? { subtitle: row.vendorEmail } : {}),
              fields: [
                { label: 'Received', value: when(row.receivedAt) },
                { label: 'Status', value: <Pill tone={paymentStatusChip(row.status).tone}>{paymentStatusChip(row.status).label}</Pill> },
                { label: 'Amount', value: row.total != null ? money.format(row.total) : '—' },
                { label: 'Contract', value: row.contractNumber || '—' },
                { label: 'Form', value: <span className="capitalize">{row.form}</span> },
              ],
              onOpen: () => setOpenId(row.id),
            }))}
          >
            {rows.map((row: PaymentSubmissionRow) => {
              const chip = paymentStatusChip(row.status);
              return (
                <tr
                  key={row.id}
                  className="cursor-pointer border-t border-portal-line align-middle hover:bg-portal-hover"
                  onClick={() => setOpenId(row.id)}
                >
                  <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">{when(row.receivedAt)}</td>
                  <td className="p-3 text-portal-small">
                    <span className="block font-bold text-portal-ink">{row.vendorName || '—'}</span>
                    {row.vendorEmail ? <span className="block text-portal-micro text-portal-muted">{row.vendorEmail}</span> : null}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted capitalize">{row.form}</td>
                  <td className="p-3 text-portal-small">{row.contractNumber || '—'}</td>
                  <td className="p-3 text-right text-portal-small tabular-nums">
                    {row.total != null ? money.format(row.total) : '—'}
                  </td>
                  <td className="p-3 text-portal-small">
                    <Pill tone={chip.tone}>{chip.label}</Pill>
                  </td>
                </tr>
              );
            })}
          </DataTable>
          <Pagination
            page={list.data.pagination.page}
            totalPages={list.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(list.data.pagination.page - 1) * PAGE_SIZE + 1}–${
              (list.data.pagination.page - 1) * PAGE_SIZE + rows.length
            } of ${list.data.pagination.total.toLocaleString()}`}
            className="mt-3"
          />
        </>
      )}

      <Drawer
        open={openId !== null}
        title={detail.data?.vendorName || 'Payment submission'}
        subtitle={detail.data ? `${detail.data.form} · ${when(detail.data.receivedAt)}` : undefined}
        onClose={() => setOpenId(null)}
        footer={
          detail.data ? (
            <Button variant="secondary" onClick={() => setConfirmId(detail.data!.id)}>
              <RefreshCwIcon className="size-4" aria-hidden="true" />
              Reprocess
            </Button>
          ) : null
        }
      >
        {detail.isPending ? (
          <LoadingState label="Loading…" />
        ) : detail.isError ? (
          <ErrorNotice title="Could not load" message={errorMessage(detail.error)} />
        ) : detail.data ? (
          <div>
            <Field label="Status">
              <Pill tone={paymentStatusChip(detail.data.status).tone}>
                {paymentStatusChip(detail.data.status).label}
              </Pill>
            </Field>
            <Field label="Source">{detail.data.source}</Field>
            <Field label="Form">{detail.data.form}</Field>
            <Field label="Contract">{detail.data.contractNumber || '—'}</Field>
            <Field label="Vendor email">{detail.data.vendorEmail || '—'}</Field>
            <Field label="Amount">{detail.data.total != null ? money.format(detail.data.total) : '—'}</Field>
            <Field label="Expenses created">
              {detail.data.outcome?.expensesCreated ? `Yes (${detail.data.outcome.created ?? 0})` : 'No'}
            </Field>
            <Field label="Statement">
              {detail.data.outcome?.statementId ? (
                <a
                  href={salesforceRecordUrl(detail.data.outcome.statementId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand"
                >
                  Open in Salesforce <ExternalLinkIcon className="size-3.5" aria-hidden="true" />
                </a>
              ) : (
                '—'
              )}
            </Field>
            <Field label="QuickBooks bill">
              {detail.data.qbo?.qboBillId
                ? `#${detail.data.qbo.qboBillId}${detail.data.qbo.receiptsAttached ? ` · ${detail.data.qbo.receiptsAttached} receipts` : ''}`
                : detail.data.qbo?.status === 'failed'
                  ? `Failed: ${detail.data.qbo.error ?? 'unknown'}`
                  : 'Not pushed'}
            </Field>
            {detail.data.outcome?.error ? (
              <Field label="Error">
                <span className="text-rose-700">{detail.data.outcome.error}</span>
              </Field>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmId !== null}
        title="Reprocess this payment?"
        message="This replays the submission. Salesforce expenses that already succeeded are not re-created; the QuickBooks push is idempotent — only steps that failed are retried."
        confirmLabel="Reprocess"
        pending={reprocess.isPending}
        error={reprocess.isError ? errorMessage(reprocess.error) : undefined}
        onConfirm={() => {
          if (confirmId) reprocess.mutate(confirmId);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'leads', label: 'Leads' },
  { key: 'payments', label: 'Payments' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * Ops view of the two intake pipelines: website-form leads and payment-request
 * submissions. See what came in, how it processed, retry a failed job, and
 * release a lead the spam gate held. Gated on the viewAnalytics permission (the
 * API enforces it too); the nav hides it otherwise.
 *
 * @returns The submissions page.
 */
export function SubmissionsPage(): React.ReactElement {
  const [tab, setTab] = useState<TabKey>('leads');
  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="OPERATIONS"
        title="Submissions"
        subtitle="Every website lead and payment request, with how it processed — review what came in, retry a failed job, or release a lead held as spam."
      />
      <div className="mb-4">
        <SegmentedControl<TabKey>
          label="Pipeline"
          value={tab}
          onChange={setTab}
          options={TABS.map((t) => ({ key: t.key, label: t.label }))}
        />
      </div>
      {tab === 'leads' ? <LeadSubmissionsTab /> : <PaymentSubmissionsTab />}
    </section>
  );
}
